import React, { useContext, useEffect, useState } from "react";
import { ThemeContext } from "../theme";
import { Users, HeartHandshake, ShieldAlert } from "lucide-react";

function CommunityGuidelines() {
    const { colors } = useContext(ThemeContext)!;
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        setVisible(true);
    }, []);

    return (
        <div className={`min-h-screen ${colors.bgSecondaryColor}`}>
            <div className="container mx-auto px-6 py-4 md:py-20 max-w-5xl">
                <div
                    className={`transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                        }`}
                >
                    <h1 className={`text-4xl md:text-5xl font-black ${colors.textColor} mb-6`}>
                        Community Guidelines
                    </h1>

                    <p className={`${colors.textColor} opacity-70 mb-10`}>
                        Our goal is simple: make I Call On fun, fair, and welcoming for everyone.
                    </p>

                    <section className="space-y-10 text-lg leading-relaxed">
                        {/* Welcome */}
                        <div>
                            <div className="flex items-center gap-3 mb-3">
                                <HeartHandshake className="h-6 w-6 text-green-500" />
                                <h2 className={`text-2xl font-bold ${colors.textColor}`}>
                                    Welcome to the Community
                                </h2>
                            </div>
                            <p className={`${colors.textColor} opacity-80`}>
                                I Call On is built around friendly competition, creativity, and quick thinking.
                                These guidelines exist to ensure every player can enjoy the game without fear
                                of harassment, abuse, or unfair play.
                            </p>
                        </div>

                        {/* Be respectful */}
                        <div>
                            <div className="flex items-center gap-3 mb-3">
                                <Users className="h-6 w-6 text-blue-500" />
                                <h2 className={`text-2xl font-bold ${colors.textColor}`}>
                                    Be Respectful
                                </h2>
                            </div>
                            <p className={`${colors.textColor} opacity-80`}>
                                Treat other players with respect at all times. Friendly banter is fine, but
                                harassment is not.
                            </p>
                            <ul className={`${colors.textColor} opacity-80 list-disc list-inside mt-3 space-y-2`}>
                                <li>No hate speech, slurs, or discriminatory language</li>
                                <li>No personal attacks, bullying, or threats</li>
                                <li>No sexually explicit or disturbing content</li>
                            </ul>
                        </div>

                        {/* Fair play */}
                        <div>
                            <div className="flex items-center gap-3 mb-3">
                                <ShieldAlert className="h-6 w-6 text-purple-500" />
                                <h2 className={`text-2xl font-bold ${colors.textColor}`}>
                                    Play Fair
                                </h2>
                            </div>
                            <p className={`${colors.textColor} opacity-80`}>
                                I Call On rewards creativity and speed. Cheating undermines the experience for
                                everyone.
                            </p>
                            <ul className={`${colors.textColor} opacity-80 list-disc list-inside mt-3 space-y-2`}>
                                <li>Do not use bots, scripts, or external tools</li>
                                <li>Do not exploit bugs or loopholes</li>
                                <li>Do not coordinate unfair advantages with other players</li>
                            </ul>
                        </div>

                        {/* Content */}
                        <div>
                            <h2 className={`text-2xl font-bold ${colors.textColor} mb-3`}>
                                Appropriate Content
                            </h2>
                            <p className={`${colors.textColor} opacity-80`}>
                                Words submitted during gameplay should be appropriate for a general audience.
                                While creativity is encouraged, offensive or deliberately harmful content is not.
                            </p>
                        </div>

                        {/* Enforcement */}
                        <div>
                            <h2 className={`text-2xl font-bold ${colors.textColor} mb-3`}>
                                Enforcement
                            </h2>
                            <p className={`${colors.textColor} opacity-80`}>
                                Violations of these guidelines may result in one or more of the following actions:
                            </p>
                            <ul className={`${colors.textColor} opacity-80 list-disc list-inside mt-3 space-y-2`}>
                                <li>Content removal or score invalidation</li>
                                <li>Temporary suspension from the Service</li>
                                <li>Permanent account termination for serious or repeated violations</li>
                            </ul>
                        </div>

                        {/* Reporting */}
                        <div>
                            <h2 className={`text-2xl font-bold ${colors.textColor} mb-3`}>
                                Reporting Issues
                            </h2>
                            <p className={`${colors.textColor} opacity-80`}>
                                If you encounter behavior that violates these guidelines, please report it through
                                the app or contact support. Reports help keep the community healthy and fun.
                            </p>
                        </div>

                        {/* Final */}
                        <div className={`${colors.cardBg} ${colors.borderColor} border rounded-2xl p-8`}>
                            <p className={`${colors.textColor} opacity-80`}>
                                These guidelines are part of our commitment to building a respectful and enjoyable
                                space for players around the world. By playing I Call On, you agree to uphold these
                                standards and help create a positive community.
                            </p>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}

export default CommunityGuidelines;
